/**
 * Compresses an image file (File object) to a target size/quality using HTML Canvas.
 * Supports standard image types (JPEG, PNG, WEBP).
 * Falls back to original file if compression fails or canvas is unsupported.
 * 
 * @param file - The original File object
 * @param maxWidth - Maximum width in pixels (default: 1024)
 * @param quality - JPEG compression quality 0-1 (default: 0.7)
 * @returns Promise<File> - The compressed file (or original if error/no change)
 */
export const compressImage = async (file: File, maxWidth = 1024, quality = 0.7): Promise<File> => {
    // If not an image, return original
    if (!file.type.startsWith('image/')) {
        return file;
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            const img = new Image();

            img.onload = () => {
                try {
                    // Calculate new dimensions
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }

                    // Create canvas
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;

                    // Draw image
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        resolve(file); // Canvas context not available
                        return;
                    }

                    // Better smoothing
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, width, height);

                    // Compress to JPEG Blob
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                resolve(file);
                                return;
                            }

                            // If compressed blob is LARGER than original (rare but possible with transparent png -> jpg), keep original
                            if (blob.size > file.size) {
                                resolve(file);
                                return;
                            }

                            // Create new File object
                            const compressedFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now(),
                            });

                            console.log(`[ImageCompression] Compressed: ${(file.size / 1024).toFixed(2)}KB -> ${(compressedFile.size / 1024).toFixed(2)}KB`);
                            resolve(compressedFile);
                        },
                        'image/jpeg',
                        quality
                    );
                } catch (error) {
                    console.error('[ImageCompression] Error during compression:', error);
                    resolve(file); // Fail safe
                }
            };

            img.onerror = (err) => {
                console.error('[ImageCompression] Error loading image:', err);
                resolve(file); // Fail safe
            };

            if (event.target?.result) {
                img.src = event.target.result as string;
            } else {
                resolve(file);
            }
        };

        reader.onerror = (err) => {
            console.error('[ImageCompression] FileReader error:', err);
            resolve(file);
        };

        reader.readAsDataURL(file);
    });
};
